import React, {useMemo} from 'react';
import {Box, IconButton} from '@mui/material';
import {OpenInNew} from '@mui/icons-material';
import {
    DataGridColumn,
    DataGridColumnAlignments,
    DataGridColumnTypes,
    DataGridColumnWidthTypes,
    DataGridDensities,
    DataGridRow,
    useDataGridSavedState
} from '../../../../../../../../../packages/data-grid';
import PartnerDataGrid from '../../../../../../../../containers/data-grid';
import {SearchDataSettingsOrderBy, SearchDataSettingsPaginationInfo} from '../../../../../../../../hooks/use-search';
import {DashboardWidgetLayoutConfig, DashboardWidgetOrderByField} from "../../../types";
import './index.scss';
import {GetMinHeightReturnValue} from "../../../dashboard-widget.config";
import DashboardWidgetFooter from "../../dashboard-widget/footer";

const dataGridSaveConfig = (id: string) => ({
    name: `Dash_Widget_${id}`,
    version: 2,
});

const dataGridColumnNames = {
    navigate: '__navigate__',
} as const;

/**
 * Base data type for table rows
 */
export interface TableDataPoint {
    [key: string]: any;
}

/**
 * Configuration for column rendering and formatting
 */
export interface ColumnConfig {
    key: string;           // The key in the data object
    title: string;         // Column header title
    width?: number;        // Column width (default: 80)
    align?: DataGridColumnAlignments;  // Column alignment
    format?: (value: any, entry?: any) => React.ReactNode;  // Optional formatter
    sortable?: boolean;    // Whether column is sortable
    pinned?: boolean;      // Whether column is pinned
    hidden?: boolean;      // Whether column is hidden by default
}

export type BottomRowColumnValueArgs = { col: ColumnConfig, data: TableDataPoint[] };

export interface BottomRowColumn {
    key: string;           // The key in the data object
    value: (args: BottomRowColumnValueArgs) => React.ReactNode; // Function to render the value for this column
}

export interface BottomRow {
    columns: BottomRowColumn[];
}

/**
 * Configuration for table pagination
 */
export interface PaginationConfig {
    enabled: boolean;
    pageSize?: number;
}

/**
 * Configuration for table navigation
 */
export interface NavigationConfig {
    enabled: boolean;
    onClick: (key: string, data: TableDataPoint) => void;
}

/**
 * Complete table configuration
 */
export interface TableConfig {
    columns: ColumnConfig[];
    visibleRows?: number;
    bottomRow?: BottomRow,
    getKey: (data: TableDataPoint) => string | number;
    pagination?: PaginationConfig;
    navigation?: NavigationConfig;
    density?: DataGridDensities;
    saveState?: boolean;
}

export interface TableWidgetProps {
    data: TableDataPoint[];
    config: TableConfig;
    identifier: string;
    isLoading: boolean;
    paginationInfo?: SearchDataSettingsPaginationInfo;
    setPaginationInfo: React.Dispatch<React.SetStateAction<SearchDataSettingsPaginationInfo | undefined>>;
    isEnabled?: boolean;
    orderBy?: SearchDataSettingsOrderBy;
    setOrderBy?: React.Dispatch<React.SetStateAction<SearchDataSettingsOrderBy | undefined>>;
    orderByFields?: DashboardWidgetOrderByField[];
    hasPagination?: boolean;
    underlyingDataVisibility?: DashboardWidgetLayoutConfig['underlyingDataVisibility'];
    minHeight: GetMinHeightReturnValue,
}

/**
 * Table Widget Component
 */
export const DashboardTableWidget: React.FC<TableWidgetProps> = ({
                                                                     data,
                                                                     config,
                                                                     identifier,
                                                                     isLoading,
                                                                     paginationInfo,
                                                                     setPaginationInfo,
                                                                     orderBy,
                                                                     setOrderBy,
                                                                     isEnabled = true,
                                                                     orderByFields = [],
                                                                     hasPagination,
                                                                     underlyingDataVisibility,
                                                                     minHeight,
                                                                 }) => {
    const {getKey: configGetKey, bottomRow} = config;

    const hasBottomRow = useMemo(() => !!config.bottomRow, [config.bottomRow]);

    const saveConfig = useMemo(() => dataGridSaveConfig(identifier), [identifier]);

    const columns = useMemo<Partial<DataGridColumn>[]>(() => {
        const res: Partial<DataGridColumn>[] = [];
        const orderByFieldsSet = new Set(orderByFields.map(field => field.name));

        // Add navigation column if enabled
        if (config.navigation?.enabled) {
            res.push({
                name: dataGridColumnNames.navigate,
                title: "",
                width: 0,
                pinned: true,
                resizable: false,
                reorderable: false,
                pinnedToggleable: false,
                visibilityToggleable: false,
                sortable: false,
            });
        }

        // Add configured columns
        for (const col of config.columns) {
            res.push({
                name: col.key,
                title: col.title,
                type: DataGridColumnTypes.element,
                width: {size: col.width ?? 80, minWidth: 0, type: DataGridColumnWidthTypes.default},
                alignment: col.align ?? DataGridColumnAlignments.left,
                resizable: true,
                reorderable: true,
                pinnedToggleable: true,
                visibilityToggleable: true,
                sortable: col.sortable ?? orderByFieldsSet.has(col.key),
                pinned: col.pinned,
                visible: !col.hidden,
            });
        }

        return res.filter(e =>
            underlyingDataVisibility?.[e.name ?? ""] === undefined ||
            underlyingDataVisibility?.[e.name ?? ""] === null ||
            underlyingDataVisibility?.[e.name ?? ""]
        )
    }, [orderByFields, config.navigation?.enabled, config.columns, underlyingDataVisibility]);


    const [dataGridColumns] = useDataGridSavedState({
        columns: columns as DataGridColumn[],
        version: saveConfig.version,
        name: saveConfig.name,
    });

    const dataGridRows = useMemo(() => {
        const rows = data.map(entry => {
            const key = configGetKey(entry);
            const row: DataGridRow = {
                key,
                data: entry,
                cells: {}
            };

            // Add navigation cell if enabled
            if (config.navigation?.enabled) {
                row.cells[dataGridColumnNames.navigate] = (
                    <IconButton
                        color="secondary"
                        onClick={() => config.navigation?.onClick(key.toString(), entry)}
                    >
                        <OpenInNew className="icon normal"/>
                    </IconButton>
                );
            }

            // Add data cells
            for (const col of config.columns) {
                const value = entry[col.key];
                row.cells[col.key] = col.format ? col.format(value, entry) : value;
            }

            return row;
        });
        if (hasBottomRow) {
            const row: DataGridRow = {
                key: 'bottom-row',
                className: 'data-grid-bottom-row',
                data: {},
                cells: {}
            };
            // Add navigation cell if enabled
            if (config.navigation?.enabled) {
                row.cells[dataGridColumnNames.navigate] = ' '; // we just need a placeholder
            }
            // Add data cells
            for (const col of config.columns) {
                const renderer = bottomRow!.columns.find(e => e.key === col.key)?.value;
                row.cells[col.key] = () => renderer?.({col: col, data: data}) ?? ' ';
            }
            rows.push(row);
        }
        return rows;
    }, [data, hasBottomRow, configGetKey, config.navigation, config.columns, bottomRow]);

    if (!isEnabled) return null;

    return (
        <>
            <Box
                // @ts-ignore
                sx={{
                    ...(isLoading && {
                        '.dashboard-widget-table': {
                            height: minHeight,
                            '.data-grid-layout': {
                                height: (theme) => typeof minHeight === 'number'
                                    ? `${minHeight}px !important`
                                    : typeof minHeight === 'string'
                                        ? `${minHeight} !important`
                                        : typeof minHeight === 'object'
                                            ? minHeight !== null
                                                ? Object.entries(minHeight).reduce((acc, [key, value]) => {
                                                    acc[key] = `${value}px !important`;
                                                    return acc;
                                                }, {})
                                                : minHeight
                                            : typeof minHeight === 'function'
                                                ? minHeight(theme)
                                                : minHeight
                            },
                            '.data-grid-loading-container': {
                                minHeight: '200px !important',
                            },
                            '.data-grid-empty-content-container': {
                                minHeight: '200px !important',
                            },
                        },
                    }),
                    ...((!data.length && !isLoading) && {
                        '.dashboard-widget-table': {
                            '.data-grid-layout': {
                                height: '380px !important',
                            },
                            '.data-grid-loading-container': {
                                height: '380px !important',
                            },
                            '.data-grid-empty-content-container': {
                                minHeight: '300px !important',
                                height: '300px !important',
                            },
                        },
                    }),
                }}
            >
                <PartnerDataGrid
                    {...saveConfig}
                    hideExporting
                    hideToolbar
                    hideFooterActions
                    hidePageSize
                    hideFooter
                    hidePagination
                    classNames={{container: 'dashboard-widget-table', loading: 'my-0'}}
                    initialLayoutState={{columns: columns as DataGridColumn[]}}
                    columns={dataGridColumns}
                    rows={dataGridRows}
                    getPaginationText={(pagination) => pagination ? `Page ${pagination.currentPage}` : ''}
                    visibleRows={config.visibleRows ?? 8}
                    state={{
                        sortBy: orderBy,
                        pagination: {currentPage: 1, pageSize: data.length, length: data.length},
                        loading: {state: false, count: 1, animationName: 'fade', animationTimeout: 300},
                        density: config.density ?? DataGridDensities.compact,
                    }}
                    onSortByChanged={(_, current) => setOrderBy?.(current)}
                />
            </Box>
            <Box sx={{paddingInline: 2}}>
                <DashboardWidgetFooter
                    paginationInfo={paginationInfo}
                    setPaginationInfo={setPaginationInfo}
                    isLoading={isLoading}
                    orderBy={orderBy}
                    setOrderBy={setOrderBy}
                    orderByFields={orderByFields}
                    hasPagination={hasPagination}
                    data={data}
                />
            </Box>
        </>
    );
};

export default DashboardTableWidget; 